import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { Bell, BellOff, BellRing } from "lucide-react";
import api from "@/lib/api";

function urlB64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

const supported =
  typeof window !== "undefined" &&
  "serviceWorker" in navigator &&
  "PushManager" in window &&
  "Notification" in window;

export default function NotificationToggle({ variant = "ghost" }) {
  const [status, setStatus] = useState("idle"); // idle | on | off | working
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!supported) return;
    navigator.serviceWorker.ready
      .then((reg) => reg.pushManager.getSubscription())
      .then((sub) => setStatus(sub ? "on" : "off"))
      .catch(() => setStatus("off"));
  }, []);

  const enable = async () => {
    if (!supported) {
      toast.error("Tu navegador no soporta notificaciones push. Instala la app en tu teléfono.");
      return;
    }
    setBusy(true);
    try {
      const perm = await Notification.requestPermission();
      if (perm !== "granted") {
        toast.error("Permiso de notificaciones denegado. Actívalo en los ajustes del navegador.");
        setBusy(false);
        return;
      }
      const reg = await navigator.serviceWorker.ready;
      const { data } = await api.get("/push/vapid-public");
      if (!data.publicKey) {
        toast.error("No hay configuración de notificaciones en el servidor.");
        setBusy(false);
        return;
      }
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlB64ToUint8Array(data.publicKey),
      });
      const json = sub.toJSON();
      await api.post("/push/subscribe", { endpoint: json.endpoint, keys: json.keys });
      setStatus("on");
      toast.success("Notificaciones activadas en este dispositivo");
      // aviso de prueba
      api.post("/push/test").catch((e) => console.error("Error en aviso de prueba:", e));
    } catch (e) {
      console.error("Error al activar notificaciones:", e);
      toast.error("No se pudieron activar las notificaciones.");
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await api.post("/push/unsubscribe", { endpoint: sub.endpoint, keys: sub.toJSON().keys }).catch(() => {});
        await sub.unsubscribe();
      }
      setStatus("off");
      toast.success("Notificaciones desactivadas en este dispositivo");
    } catch (e) {
      console.error("Error al desactivar notificaciones:", e);
    } finally {
      setBusy(false);
    }
  };

  const isOn = status === "on";
  const cls =
    variant === "ghost"
      ? "flex items-center gap-1.5 px-3 py-2 rounded-full font-semibold text-sm transition-colors"
      : "flex items-center justify-center gap-2 w-full py-3 rounded-full font-semibold transition-colors";

  return (
    <button
      onClick={isOn ? disable : enable}
      disabled={busy}
      data-testid="notification-toggle-btn"
      title={isOn ? "Notificaciones activas" : "Activar notificaciones"}
      className={cls}
      style={
        isOn
          ? { background: "#DCFCE7", color: "#166534" }
          : variant === "ghost"
          ? { background: "#E8F0F8", color: "#2E6CA4" }
          : { background: "#1F4E78", color: "#fff" }
      }
    >
      {isOn ? <BellRing size={variant === "ghost" ? 16 : 18} /> : <Bell size={variant === "ghost" ? 16 : 18} />}
      {isOn ? "Avisos activos" : "Activar avisos"}
    </button>
  );
}
